import React, { useState, useEffect, useRef } from 'react';
import { useGlobal } from 'context';
import { request } from 'utils/api';
import {
  Section,
  Layout,
  ErrorDialog,
  SignupStepContainer,
  Loader,
  Button,
} from 'components';

import './call.less';

import { PUBLIC_TOKEN } from 'utils/env';
import { CalendarDay } from 'components/CalendarDay';

import { Header } from 'components/SignupStepContainer';
import { withLayout } from 'helpers';
import { useTracker } from 'utils/tracking';
import useQuery from 'utils/hooks/useQuery';
import { useFeatures, useFormVariant } from 'components/FeaturesContext';
import { mapMultiSalesforceToSingle } from './utils';

async function getIsAvailableNow() {
  const { content } = await request({
    method: 'GET',
    path: `/v1/scheduler/availability/call-now`,
    headers: {
      Authorization: `Basic ${PUBLIC_TOKEN}`,
    },
  });
  return content;
}

async function getDailyAvailabilityForQueue(intentId) {
  const { content } = await request({
    method: 'GET',
    path: `/v1/scheduler/availability/queue/${intentId}`,
    headers: { Authorization: `Basic ${PUBLIC_TOKEN}` },
  });
  const items = (content.availability || content).filter((c) => c.available);

  return items;
}

function ConnectForm({ history, setError, setMessage, setAction, setLoading }) {
  const trackOnce = useTracker();
  const { account } = useGlobal();

  const intervalRef = useRef(0);

  async function queryCallStatus(callId) {
    try {
      const { content } = await request({
        method: 'GET',
        path: `/v1/scheduler/availability/call-status`,
        params: { callId },
        headers: { Authorization: `Basic ${PUBLIC_TOKEN}` },
      });

      if (['Success', 'Finished'].includes(content.callStatus)) {
        history.push('/call/success');
        return;
      } else if (content.callStatus !== 'InProgress') {
        clearInterval(intervalRef);
        setMessage(
          'We are not able to reach you at the moment, please schedule a call to talk to us.'
        );
        setAction('schedule');
      }
    } catch (e) {
      clearInterval(intervalRef);
      setError(e);
      setMessage(
        'We are not able to reach you at the moment, please schedule a call to talk to us.'
      );
      setAction('schedule');
    }
  }

  function startQueryCallStatus(callId) {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => queryCallStatus(callId), 5000);
  }

  function triggerSchedule() {
    trackOnce('lead_question21_book_call');
    setAction('schedule');
  }

  async function triggerCallNow() {
    trackOnce('lead_question21_book_call');
    setLoading(true);
    setAction('call-now');

    try {
      const { success, content } = await request({
        method: 'POST',
        path: '/v1/scheduler/availability/call-now',
        body: {
          phone: account.phone,
          email: account.email,
        },
        headers: { Authorization: `Basic ${PUBLIC_TOKEN}` },
      });

      if (!success || !content.success) {
        setLoading(false);
        setAction('schedule');
        return;
      }

      setLoading(false);
      if (content.callId) {
        startQueryCallStatus(content.callId);
      }
    } catch (e) {
      setError(e);
      setLoading(false);
    }
  }

  return (
    <Layout center>
      <div className="connectForm">
        <h2 className="bodycopy1">
          Thank you{' '}
          {account && (
            <>
              {account.firstName} {account.lastName}
            </>
          )}
          .
        </h2>
        <p className="bodycopy2">How would you like to connect with us.</p>

        <div>
          <Button fluid onClick={triggerSchedule}>
            Schedule a call
          </Button>
        </div>
        <div>
          <Button fluid onClick={triggerCallNow}>
            Call now
          </Button>
        </div>
      </div>
    </Layout>
  );
}

function CallNow() {
  return (
    <div className="callNowForm">
      <p>
        One of our sales experts is about to start the call. Please wait ...
      </p>

      <Layout center className="loader-wrapper">
        <Loader />
      </Layout>
    </div>
  );
}

function Schedule({
  message,
  items,
  intentId,
  selected,
  setSelected,
  setLoading,
  setError,
  history,
}) {
  const trackOnce = useTracker();
  const { SHOW_PHONE_NUMBER_SCHEDULING: showPhoneNumber } = useFeatures();

  async function bookSlot(slot) {
    trackOnce('lead_question21_book_call');
    setLoading(true);
    let doctorIds = slot.doctorSlotList.map(
      (slotItem) => slotItem.slotExternalId
    );
    try {
      await request({
        method: 'POST',
        path: '/v1/scheduler/availability/schedule-appointment',
        body: {
          intentId,
          slotExternalIds: doctorIds,
          date: slot.date,
          clientZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        headers: { Authorization: `Basic ${PUBLIC_TOKEN}` },
      });

      trackOnce('scheduled');
      history.push(`/call/booked?timeslot=${Date.parse(slot.utcDateTime)}`);
    } catch (e) {
      setError(e);
      setLoading(false);
    }
  }

  return (
    <div className="stepScheduleAppointment__content">
      <div></div>
      <h1 className="stepScheduleAppointment__copy">
        {showPhoneNumber &&
          "Alternatively, if you'd like to schedule for a later date, "}
        {showPhoneNumber ? 'p' : 'P'}lease select the day and time that is the
        most convenient for you.
      </h1>

      {message && <p className="bodycopylong">{message}</p>}

      <p className="bodycopylong">A call lasts 15 minutes.</p>

      <div>
        {items?.length === 0 && (
          <p className="bodycopylong">No slots available</p>
        )}
        {(items || []).map((item) => {
          return (
            <CalendarDay
              onOpen={(open) => {
                setSelected(open ? item.date : null);
              }}
              bookSlot={bookSlot}
              intentId={intentId}
              key={item.date}
              open={item.date === selected}
              {...item}
            />
          );
        })}
      </div>
    </div>
  );
}

function PhoneNumberHeader() {
  return (
    <div
      className="stepScheduleAppointment__copy"
      style={{ marginTop: '10px' }}>
      <div>If you're ready, talk to one of our dedicated team members now</div>

      <Button
        style={{
          margin: '1em auto 4em auto',
          display: 'block',
          textAlign: 'center',
        }}>
        <a title="Click to call" href="tel:8667752496" className="phone">
          (866) 775-2496
        </a>
      </Button>
    </div>
  );
}

function Call(props) {
  const context = useGlobal();
  const query = useQuery();
  const { SHOW_PHONE_NUMBER_SCHEDULING: showPhoneNumber } = useFeatures();
  const _formVariant = useFormVariant();

  // State
  const [action, setAction] = useState();
  const [intentId, setIntentId] = useState(query.get('intent'));
  const [items, setItems] = useState();
  const [selected, setSelected] = useState();

  const [message, setMessage] = useState();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();

  // Lifecycle
  useEffect(() => {
    window.scrollTo({ top: 0 });
    window?.gtag('event', 'view_call');
    window?.gtag('event', 'submitted_account_form');

    if (intentId) {
      getExistingQueue();
    } else {
      getQueue();
    }
  }, []);

  // Handlers
  async function getExistingQueue() {
    try {
      setSelected(items[0]?.date);
      setItems(await getDailyAvailabilityForQueue(intentId));
      setLoading(false);
      setAction('schedule');
    } catch (e) {
      setError(e);
    }
  }

  async function getQueue() {
    const { account, leadQuestions, startInfo } = context;

    const singleQuestions = leadQuestions.filter(
      (q) => q.multiSalesforce === false || q.multiSalesforce === undefined
    );

    const questions = singleQuestions
      .filter((q) => !q.disqualifying)
      .map((c) => {
        return {
          flag: c.id,
          answer: Array.isArray(c.answer) ? c.answer.join(', ') : c.answer,
          salesforceId: c.salesforceId,
        };
      });

    const multiQuestions = leadQuestions.filter((q) => q.multiSalesforce);
    questions.push(...mapMultiSalesforceToSingle(multiQuestions));

    let variant = window.localStorage.getItem('variant') || undefined;
    let formVariant =
      window.localStorage.getItem('form_variant') || 'Nue life lead form';
    let practiceCode =
      window.sessionStorage.getItem('practiceCode') || 'nuelife';

    try {
      const { content } = await request({
        method: 'POST',
        path: '/v1/router/web-form',
        body: {
          gender: account.gender,
          firstName: account.firstName,
          idmeGroup: account.idmeGroup,
          idmeUuid: account.idmeUuid,
          idmeVerificationAttempt: account.idmeVerificationAttempt,
          lastName: account.lastName,
          email: account.email.toLowerCase().trim(),
          entheaCompany: startInfo.entheaCompany,
          formVariant: _formVariant,
          phone: account.phone,
          dateOfBirth: startInfo.dateOfBirth,
          billingAddress: account.billingAddress,
          createLead: false,
          shippingAddress: account.shippingAddress,
          zipCode: account.zipCode,
          entry_origin: window.sessionStorage.entry_origin,
          utmSource: window.localStorage.utm_source,
          utmMedium: window.localStorage.utm_medium,
          utmTerm: window.localStorage.utm_term,
          utmContent: window.localStorage.utm_content,
          utmCampaign: window.localStorage.utm_campaign,
          gclid: window.localStorage.gclid,
          trafficType: window.localStorage.traffic_source,
          state: startInfo.state,
          landingPageVariant:
            `${formVariant}` +
            (variant != undefined ? ` [variant: ${variant}]` : '') +
            (practiceCode != 'nuelife' ? ` [practice: ${practiceCode}]` : ''),
          /* Answers */
          //...answers,
          redFlagAnswers: [],
          leadQuestions: questions,
          recaptchaToken: 'empty',
          practiceCode,
        },
        headers: { Authorization: `Basic ${PUBLIC_TOKEN}` },
      });

      const parsedUrl = new URL(content);
      const intentId = parsedUrl.searchParams.get('intent');
      if (!intentId) {
        throw new Error('API: No queue found');
      }

      const items = await getDailyAvailabilityForQueue(intentId);

      if (intentId) {
        window.sessionStorage.setItem('intent', intentId);
      }

      if (!(await getIsAvailableNow())) {
        setAction('schedule');
      }

      setIntentId(intentId);
      setSelected(items[0]?.date);
      setItems(items);
      setLoading(false);
    } catch (e) {
      setError(e);
    }
  }

  return (
    <>
      <div className="stepCall">
        {error && (
          <ErrorDialog
            title="Oops something went wrong"
            message={error.message}
            onClose={() => setError(undefined)}
          />
        )}

        <Section changeyellow style={{ borderRadius: 0 }}>
          <>
            <div style={{ paddingBottom: '2em' }}></div>
            <SignupStepContainer
              header={
                <Header>
                  <p className="bodycopy3">
                    {showPhoneNumber
                      ? `
                        Last step: get in touch with our dedicated experts in
                        at-home ketamine therapy however works for you. This is
                        the next step in discovering if this program is right
                        for you.
                      `
                      : `
                          Last step: schedule a call with our Welcome Team
                          —experts in at-home ketamine therapy— to learn a little
                          bit more about you and your goals.
                      `}
                  </p>
                </Header>
              }>
              <Layout stackable>
                {!loading && !action && (
                  <ConnectForm
                    history={props.history}
                    setError={setError}
                    setAction={setAction}
                    setLoading={setLoading}
                    setMessage={setMessage}
                  />
                )}

                {showPhoneNumber && <PhoneNumberHeader />}

                {!loading && action === 'call-now' && <CallNow />}

                {!loading && action === 'schedule' && (
                  <Schedule
                    // Terrible prop drilling, fix someday in the future please
                    history={props.history}
                    message={message}
                    items={items}
                    intentId={intentId}
                    selected={selected}
                    setSelected={setSelected}
                    setLoading={setLoading}
                    setError={setError}
                  />
                )}

                {loading && (
                  <Layout center className="loader-wrapper">
                    <Loader />
                  </Layout>
                )}
              </Layout>
            </SignupStepContainer>
          </>
        </Section>
      </div>
    </>
  );
}

export default withLayout(Call);